/**
 * API client — typed wrapper around fetch for the DentAI backend.
 *
 * All API calls go through this module, which:
 * - Adds the Authorization header from stored tokens
 * - Adds the Content-Type header
 * - Handles errors consistently
 * - Returns typed responses
 */

import { clientError, currentLocale, localizeApiError, localizeRetryAfter } from './error-localization.js';

import type { ApiResponse, HealthStatus, AuthTokens, BusinessHours } from '@dentai/shared';
import type * as Shared from '@dentai/shared';

export interface ClinicEmailIdentity { id: number; clinicId: number; organizationId: number; localPart: string; address: string; domain: string; status: 'pending_activation' | 'active'; suggestion: string }

export interface ClinicalHistoryItem {
  id: number | null;
  organizationId: number;
  clinicId: number;
  patientId: number;
  content: string;
  updatedBy: number | null;
  updaterName: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}
export interface DiagnosticAttachmentItem { id:number; patientId:number; originalFilename:string; mimeType:string; sizeBytes:number; attachmentType:'xray'|'ct'|'clinical_photo'|'report'|'other'; description:string|null; uploadedBy:number; uploaderName:string|null; createdAt:string }

// ─── Conversation Types ─────────────────────────────────────────────────────

export interface ConversationItem {
  id: number;
  clinicId: number;
  organizationId: number;
  patientId: number | null;
  channel: string;
  externalId: string | null;
  status: string;
  assignedTo: number | null;
  aiActive: boolean;
  agentOwner: 'agent' | 'staff' | 'shared' | 'escalated';
  agentLanguage: string | null;
  agentIntent: string | null;
  agentOutcome: string | null;
  agentPaused: boolean;
  agentEscalationReason: string | null;
  agentOwnership: 'agent' | 'staff' | 'unassigned';
  agentOperationalState: 'active' | 'paused' | 'needs_staff' | 'resolved';
  agentControlReason: string | null;
  agentControlAt: string | null;
  agentControlBy: number | null;
  lastMessageAt: string | null;
  subject: string | null;
  createdAt: string;
  updatedAt: string;
  patientName?: string | null;
  latestMessagePreview?: string | null;
  unreadCount?: number;
  reviewStatus?: 'none' | 'awaiting_review';
  senderAddress?: string | null;
  receivedAt?: string | null;
  relatedPurpose?: string | null;
}

export interface MessageItem {
  id: number;
  conversationId: number;
  clinicId: number;
  organizationId: number;
  direction: string;
  senderType: string;
  senderId: number | null;
  body: string | null;
  contentType: string;
  externalMessageId: string | null;
  senderAddress?: string | null;
  inReplyTo?: string | null;
  deliveryStatus: string;
  deliveredAt: string | null;
  readAt: string | null;
  createdAt: string;
}

export interface CareJourneyItem {
  id: number;
  label: string;
  status: 'planned' | 'scheduled' | 'completed' | 'cancelled';
}

export interface CareJourneyAppointment {
  id: number;
  appointmentId: number;
  careJourneyItemId: number | null;
  startTime: string;
  endTime: string;
  status: string;
}

export interface CareJourney {
  id: number;
  organizationId: number;
  clinicId: number;
  patientId: number;
  patientName: string;
  administrativeSummary: string;
  status: 'proposed' | 'accepted' | 'declined' | 'scheduled' | 'in_progress' | 'completed' | 'archived';
  administrativeValue: number | null;
  followUpStatus: 'none' | 'due' | 'completed';
  followUpDueAt: string | null;
  acceptedAt: string | null;
  acceptedBy: number | null;
  revenueOpportunityId: number | null;
  source: string;
  createdBy: number | null;
  createdAt: string;
  updatedAt: string;
  items: CareJourneyItem[];
  appointments: CareJourneyAppointment[];
}

export interface CareJourneyMetrics {
  total: number;
  byStatus: Record<string, number>;
  acceptedValue: number;
  completedValue: number;
  dueFollowUps: number;
  plannedAppointments: number;
  completedAppointments: number;
}

export interface PmsImportSummary {
  clinic: { updated: number; unchanged: number };
  patients: { created: number; linkedExisting: number; alreadyImported: number };
  providers: { created: number; linkedExisting: number; alreadyImported: number };
  appointmentTypes: { created: number; linkedExisting: number; alreadyImported: number };
  appointments: { created: number; linkedExisting: number; alreadyImported: number };
  revenueBaselineAppointments: number;
}

export interface PmsImportIssue {
  line: number;
  field: string;
  message: string;
}

export interface PmsImportPreview {
  sourceFormat: string;
  sourceFingerprint: string;
  sourceSizeBytes: number;
  rowCount: number;
  canConfirm: boolean;
  confirmationToken: string | null;
  errors: PmsImportIssue[];
  warnings: PmsImportIssue[];
  summary: PmsImportSummary;
  samples: Record<string, Array<{ line: number; externalId: string; label: string; disposition: 'create' | 'existing' }>>;
}

export interface PmsImportBaseline {
  version: 1;
  capturedAt: string;
  importJobId: number;
  recoveryEligibleAppointments: number;
  operational: {
    appointmentCounts: Record<string, number>;
    conversationCounts: Record<string, number>;
    activeProviders: number;
    totalPatients: number;
    pendingAppointments: number;
    openConversations: number;
    aiEscalations: number;
    totalMessages: number;
    dateRange: { startDate: string | null; endDate: string | null };
  };
}

export interface PmsPilotActivation {
  status: 'not_activated' | 'active';
  baselineJobId: number | null;
  activatedAt: string | null;
  activatedBy: number | null;
  baseline: PmsImportBaseline | null;
}

export interface PmsImportResult {
  jobId: number;
  status: 'completed';
  idempotent: boolean;
  summary: PmsImportSummary;
  baseline: PmsImportBaseline | null;
}

export interface PilotEntitlement {
  tier: 'pilot' | null;
  status: 'active' | 'missing' | 'expired' | 'inactive';
  active: boolean;
  managed: boolean;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  subscriptionId: number | null;
}

export interface ManagedPilotGrant {
  subscription: { id: number; tier: 'pilot'; status: string; currentPeriodStart: string | null; currentPeriodEnd: string | null };
  entitlement: PilotEntitlement;
  idempotent: boolean;
}

export interface BillingSubscriptionStatus {
  tier: 'free' | 'pilot' | 'starter' | 'professional' | 'enterprise';
  status: 'active' | 'past_due' | 'canceled' | 'trialing' | 'incomplete';
  cancelAtPeriodEnd: boolean;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  billingCycle: 'monthly' | 'annual' | null;
  hasBillingAccount: boolean;
  trialEndsAt: string | null;
}

export interface ProfessionalCheckoutSession {
  sessionId: string;
  url: string;
}

export interface StaffInvitationItem {
  id: number;
  email: string;
  role: 'staff' | 'provider';
  status: 'pending' | 'accepted' | 'cancelled' | 'expired';
  clinicId: number;
  organizationId: number;
  expiresAt: string;
  createdAt: string;
  lastDeliveryRequestedAt: string | null;
  deliveryStatus: 'pending_delivery';
}

export interface StaffMemberItem {
  userId: number;
  email: string;
  name: string;
  role: string;
  permissions: string[];
  status: string;
  clinicId: number;
  organizationId: number;
  createdAt: string;
}

export interface WhatsAppConnectionMetadata {
  id: number;
  status: 'pending' | 'connected' | 'disconnected' | 'error';
  businessAccountIdSuffix: string;
  phoneNumberIdSuffix: string;
  accessTokenExpiresAt: string | null;
  hasError: boolean;
  disconnectedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface WhatsAppConnectionStatus {
  status: WhatsAppConnectionMetadata['status'] | 'disconnected';
  connection: WhatsAppConnectionMetadata | null;
  onboardingAvailable: boolean;
  sandboxAvailable: boolean;
  sandboxConnection: boolean;
}

export interface WhatsAppOnboardingIntent {
  expiresAt: string | null;
  launch: {
    provider: 'meta_embedded_signup';
    appId: string;
    configurationId: string;
    apiVersion: string;
  };
}

interface NotificationItem {
  id: number;
  clinicId: number;
  organizationId: number;
  userId: number;
  type: string;
  priority: string;
  title: string;
  body: string;
  resourceType: string | null;
  resourceId: string | null;
  deliveryStatus: string;
  readAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface KbItem {
  id: number;
  clinicId: number;
  organizationId: number;
  category: string;
  title: string;
  content: string;
  status: string;
  approvedBy: number | null;
  approvedAt: string | null;
  version: number;
  createdBy: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface CommunicationTemplateItem {
  id: number;
  organizationId: number;
  clinicId: number;
  templateKey: string;
  name: string;
  purpose: string;
  channel: string;
  locale: string;
  version: number;
  status: string;
  content: string;
  allowedVariables: string[];
  providerTemplateName: string | null;
  createdBy: number | null;
  approvedBy: number | null;
  approvedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CommunicationDraftItem {
  id: number;
  organizationId: number;
  clinicId: number;
  patientId: number;
  revenueOpportunityId: number | null;
  templateId: number;
  templateVersion: number;
  purpose: string;
  channel: string;
  locale: string;
  renderedBody: string;
  renderedVariables: Record<string, string | number> | null;
  authorizationMode: 'staff_approval_required' | 'automatic_policy_authorized';
  policyAuthorizedAt: string | null;
  status: string;
  createdBy: number | null;
  approvedBy: number | null;
  approvedAt: string | null;
  createdAt: string;
  updatedAt: string;
}
export interface CommunicationOutboxItem {
  id: number;
  clinicId: number;
  organizationId: number;
  communicationDraftId: number | null;
  channel: string;
  deliveryStatus: string;
  externalMessageId: string | null;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
  body?: string;
  lastAttemptAt?: string | null;
}

interface AiConfigItem {
  id: number;
  clinicId: number;
  organizationId: number;
  isEnabled: boolean;
  escalationThreshold: number;
  maxTurns: number;
  autoHandoffAfterMinutes: number;
  channelModes: { email: 'off' | 'assist' | 'auto_reply'; whatsapp: 'off' | 'assist' | 'auto_reply' };
  createdAt: string;
  updatedAt: string;
}

export interface WhatsAppAgentConfig {
  id: number;
  organizationId: number;
  clinicId: number;
  enabled: boolean;
  autonomousMode: boolean;
  mode: 'off' | 'assisted' | 'guarded_autonomous' | 'paused';
  activationAcknowledged: boolean;
  activationAcknowledgedAt: string | null;
  supportedLanguages: Array<'it' | 'en' | 'ar'>;
  fallbackLanguage: 'it' | 'en' | 'ar';
  tone: string;
  handoffDestination: string;
  operatingHoursMode: string;
  allowedActions: string[];
  escalationRules: string[];
  neverAnswerTopics: string[];
  connectionStatus: 'connected' | 'pending' | 'disconnected' | 'error';
  createdAt: string;
  updatedAt: string;
}

export interface WhatsAppAgentKnowledgeItem {
  id: number;
  organizationId: number;
  clinicId: number;
  category: string;
  locale: 'it' | 'en' | 'ar';
  title: string;
  content: string;
  status: 'draft' | 'approved' | 'archived' | 'rejected';
  approvedBy: number | null;
  approvedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface WhatsAppAgentEventItem {
  id: number;
  conversationId: number | null;
  patientId: number | null;
  messageId: number | null;
  intent: string | null;
  language: string | null;
  action: string | null;
  source: string;
  outcome: string;
  reason: string | null;
  metadata: Record<string, unknown>;
  idempotencyKey: string | null;
  sourceType: string;
  sourceLabel: string | null;
  sourceReferenceId: number | null;
  confidenceCategory: 'high' | 'needs_confirmation' | 'unavailable';
  createdAt: string;
}

export interface WhatsAppAgentEvent {
  id: number;
  conversationId: number | null;
  patientId: number | null;
  messageId: number | null;
  intent: string | null;
  language: 'it' | 'en' | 'ar' | null;
  action: string | null;
  source: string;
  outcome: string;
  reason: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface WhatsAppAgentMemoryItem {
  id: number;
  organization_id: number;
  clinic_id: number;
  patient_id: number;
  source: string;
  category: string;
  value_summary: string;
  confidence_category: 'high' | 'needs_confirmation' | 'unavailable';
  status: 'draft' | 'approved' | 'rejected' | 'archived';
  created_by_type: string;
  created_by: number | null;
  reviewed_by: number | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface WhatsAppVoiceMediaItem {
  id: number;
  organizationId: number;
  clinicId: number;
  idempotencyKey: string;
  providerEventReference: string | null;
  providerMessageReference: string | null;
  mediaReference: string | null;
  mediaType: string;
  direction: 'inbound' | 'outbound';
  receivedAt: string;
  processingState: 'pending' | 'unavailable' | 'ready_for_processing' | 'processed' | 'failed' | 'expired';
  transcriptState: 'unknown' | 'unavailable' | 'needs_staff_review' | 'available';
  detectedLanguage: 'it' | 'en' | 'ar' | null;
  dialectCategory: 'arabic_broad' | 'non_specific' | 'unknown' | 'needs_staff_confirmation';
  confidenceCategory: 'high' | 'needs_confirmation' | 'unavailable';
  escalationReason: string | null;
  retentionState: 'active' | 'expired' | 'deleted';
  retentionExpiresAt: string | null;
  patientId: number | null;
  conversationId: number | null;
  eventId: number | null;
  auditCorrelationId: string;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface AnalyticsAppt {
  total: number;
  byStatus: Record<string, number>;
  byType: Record<string, number>;
  byProvider: Record<string, number>;
  dateRange: { startDate: string | null; endDate: string | null };
}

interface AnalyticsConv {
  total: number;
  byStatus: Record<string, number>;
  byChannel: Record<string, number>;
  aiActiveCount: number;
  totalMessages: number;
  dateRange: { startDate: string | null; endDate: string | null };
}

interface AnalyticsAi {
  totalProcessed: number;
  guardrailTriggers: number;
  escalations: number;
  activations: number;
  deactivations: number;
  aiEnabled: boolean;
  configMaxTurns: number;
  dateRange: { startDate: string | null; endDate: string | null };
}

interface AnalyticsOps {
  appointmentCounts: Record<string, number>;
  conversationCounts: Record<string, number>;
  activeProviders: number;
  totalPatients: number;
  pendingAppointments: number;
  openConversations: number;
  aiEscalations: number;
  totalMessages: number;
  dateRange: { startDate: string | null; endDate: string | null };
}

interface AuditLogItem {
  id: number;
  organization_id: number;
  clinic_id: number | null;
  actor_id: number | null;
  actor_type: string;
  action: string;
  resource_type: string | null;
  resource_id: string | null;
  result: string;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

export interface RevenueOpportunityItem {
  id: number;
  organizationId: number;
  clinicId: number;
  patientId: number;
  type: string;
  priority: string;
  status: string;
  appointmentId: number | null;
  assigneeUserId: number | null;
  nextAction: 'review' | 'contact' | 'follow_up' | 'confirm_booking' | 'complete_follow_up' | null;
  dueAt: string | null;
  convertedAppointmentId: number | null;
  reason: string | null;
  metadata: Record<string, unknown> | null;
  identifiedAt: string;
  actedOnAt: string | null;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RevenueOpportunityAiAnalysis {
  providerId: 'deterministic_local' | 'live_llm';
  mode: 'deterministic_local' | 'live_llm';
  actionType: 'prepare_communication' | 'propose_booking' | 'propose_reschedule' | 'propose_cancellation' | 'staff_review' | 'no_action';
  communicationPurpose: 'appointment' | 'recall' | 'reactivation' | 'review_request' | 'operational_service' | null;
  suggestedChannel: 'internal' | 'email' | 'sms' | 'whatsapp' | null;
  suggestedNextStep: string;
  reason: string;
  confidence: number;
  usage?: { inputTokens?: number; outputTokens?: number };
}

export type AutomationPurpose = 'no_show_recovery' | 'cancelled_recovery' | 'reactivation' | 'recall' | 'review_request';
export type AutomationTrigger = 'appointment_no_show' | 'appointment_cancelled' | 'patient_inactive' | 'hygiene_recall_due' | 'appointment_completed';

export interface AutomationRuleItem {
  id: number;
  organizationId: number;
  clinicId: number;
  purpose: AutomationPurpose;
  trigger: AutomationTrigger;
  enabled: boolean;
  approvalRequired: boolean;
  cooldownMinutes: number;
  createdAt: string;
  updatedAt: string;
}

export interface RevenueOpportunityActivityItem {
  id: number;
  organizationId: number;
  clinicId: number;
  opportunityId: number;
  actorUserId: number;
  actorName: string | null;
  activityType: 'reviewed' | 'contacted' | 'follow_up' | 'booking_attempt' | 'booked' | 'completed' | 'dismissed' | 'contact_attempt';
  contactChannel: 'manual' | 'phone' | 'email' | 'whatsapp' | 'sms' | 'other' | null;
  note: string | null;
  outcome: 'no_response' | 'interested' | 'booked' | 'not_interested' | 'follow_up_needed' | 'contacted' | 'no_answer' | 'patient_declined' | 'patient_will_rebook' | 'appointment_recovered' | 'invalid_contact' | null;
  nextActionAt: string | null;
  createdAt: string;
}

export interface RecoveryPlaybookGuidance {
  recommendedAction: 'initial_contact' | 'follow_up' | 'confirm_booking' | 'review' | 'no_action';
  recommendedTiming: 'today' | 'schedule_short_follow_up' | 'scheduled' | 'none';
  guidanceStatus: 'not_started' | 'due' | 'overdue' | 'scheduled' | 'completed';
  reason: 'no_contact_attempt' | 'no_answer' | 'patient_will_rebook' | 'contacted' | 'terminal_outcome' | 'workflow_review';
  stepNumber: number;
}

export interface RecoveryTrackingSummary {
  attemptCount: number;
  lastAttempt: RevenueOpportunityActivityItem | null;
  lastOutcome: 'contacted' | 'no_answer' | 'patient_declined' | 'patient_will_rebook' | 'appointment_recovered' | 'invalid_contact' | null;
  nextActionAt: string | null;
  followUpDue: boolean;
  playbook: RecoveryPlaybookGuidance;
}

export type RecoveryFollowUpCategory = 'due_today' | 'overdue' | 'upcoming' | 'no_follow_up' | 'all_open_recovery';
export type RetentionQueueCategory = 'all' | 'recall' | 'reactivation' | 'overdue' | 'due_today' | 'upcoming' | 'no_due_date';
export interface RetentionQueueItem {
  opportunity: RevenueOpportunityItem;
  patientName: string;
  assigneeName: string | null;
  dueState: 'overdue' | 'due_today' | 'upcoming' | 'no_due_date';
  lastActivity: RevenueOpportunityActivityItem | null;
}
export interface RetentionQueue {
  items: RetentionQueueItem[];
  summary: Record<'overdue' | 'due_today' | 'upcoming' | 'no_due_date', number>;
}
export interface RecoveryFollowUpItem {
  opportunityId: number;
  patientId: number;
  patientName: string;
  type: 'no_show_recovery' | 'cancelled_recovery';
  status: 'identified' | 'contacted';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  originalAppointment: { id: number; startTime: string; status: string } | null;
  assigneeUserId: number | null;
  assigneeName: string | null;
  lastContactAttempt: RevenueOpportunityActivityItem | null;
  lastOutcome: RecoveryTrackingSummary['lastOutcome'];
  contactAttemptCount: number;
  nextActionAt: string | null;
  opportunityDueAt: string | null;
  followUpState: Exclude<RecoveryFollowUpCategory, 'all_open_recovery'>;
  playbook: RecoveryPlaybookGuidance;
}
export interface RecoveryFollowUpQueue {
  items: RecoveryFollowUpItem[];
  summary: Record<Exclude<RecoveryFollowUpCategory, 'all_open_recovery'>, number>;
}

interface RecoveryContactAttemptResult {
  opportunity: RevenueOpportunityItem;
  attempt: RevenueOpportunityActivityItem;
  tracking: RecoveryTrackingSummary;
}

interface RevenueSummary {
  bookingProof?: { recoveredAppointments: number; reactivatedPatients: number; filledSlots: number };
  recoveredValueByCurrency?: Partial<Record<'EUR' | 'USD' | 'ILS', number>>;
  total: number;
  byType: Record<string, number>;
  byStatus: Record<string, number>;
  byPriority: Record<string, number>;
  highPriority: number;
  open: number;
  booked: number;
  resolved: number;
}

interface RevenueAnalyticsResult extends RevenueSummary {
  byTypeAndStatus: Record<string, Record<string, number>>;
  recoveryRate: number;
  conversionRate: number;
  contactRate: number;
  resolutionRate: number;
  recoveryOutcomes: {
    contacted: number;
    noAnswer: number;
    declined: number;
    willRebook: number;
    recovered: number;
    attributedBooked: number;
    lost: number;
    recoveryRate: number;
  };
  dateRange: { startDate: string | null; endDate: string | null };
}

interface AuthResponse {
  tokens: AuthTokens;
  user: {
    id: number;
    email: string;
    name: string;
    role: string;
    organizationId: number;
    clinicId: number;
  };
}

export interface PendingEmailVerificationResponse {
  verificationRequired: true;
  email: string;
  deliverySent: boolean;
  cooldownSeconds: number;
}

export interface ProviderSchedule {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  isActive: boolean;
}

const API_BASE = '/api/v1';

// ─── Token Storage ──────────────────────────────────────────────────────────

let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
  if (token) {
    localStorage.setItem('dentai_access_token', token);
  } else {
    localStorage.removeItem('dentai_access_token');
  }
}

export function getAccessToken(): string | null {
  if (!accessToken) {
    accessToken = localStorage.getItem('dentai_access_token');
  }
  return accessToken;
}

export function getRefreshToken(): string | null {
  return localStorage.getItem('dentai_refresh_token');
}

export function setRefreshToken(token: string | null): void {
  if (token) {
    localStorage.setItem('dentai_refresh_token', token);
  } else {
    localStorage.removeItem('dentai_refresh_token');
  }
}

// ─── Fetch Wrapper ───────────────────────────────────────────────────────────

let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;
let onLogoutCallback: (() => void) | null = null;

export function setOnLogout(callback: (() => void) | null): void {
  onLogoutCallback = callback;
}

async function refreshAccessToken(): Promise<string | null> {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }
  isRefreshing = true;
  refreshPromise = (async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) return null;
    try {
      const res = await api.auth.refresh(refreshToken);
      if (res.error || !res.data) return null;
      setAccessToken(res.data.accessToken);
      setRefreshToken(res.data.refreshToken);
      return res.data.accessToken;
    } catch {
      return null;
    }
  })();
  try {
    return await refreshPromise;
  } finally {
    isRefreshing = false;
    refreshPromise = null;
  }
}

async function apiFetch<T>(
  path: string,
  options?: RequestInit,
  isRetry = false,
): Promise<ApiResponse<T>> {
  const token = getAccessToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (options?.headers) {
    const optHeaders = options.headers as Record<string, string>;
    for (const [k, v] of Object.entries(optHeaders)) {
      headers[k] = v;
    }
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, { ...options, headers });
  } catch {
    return { error: clientError('NETWORK_ERROR') };
  }

  // If 401 and not already retrying, attempt refresh + retry
  if (response.status === 401 && !isRetry) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      // Retry the original request with the new token
      return apiFetch<T>(path, options, true);
    }
    // Refresh failed — clear tokens and redirect to login
    setAccessToken(null);
    setRefreshToken(null);
    if (onLogoutCallback) onLogoutCallback();
    return { error: localizeApiError({ code: 'UNAUTHORIZED', message: 'Session expired' }, currentLocale()) };
  }

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    return { error: clientError('SERVICE_UNAVAILABLE') };
  }

  try {
    const payload = await response.json() as ApiResponse<T>;
    if (!payload.error) return payload;
    const error = localizeApiError(payload.error);
    const retryAfter = response.status === 429 ? Number(response.headers.get('retry-after')) : 0;
    return { ...payload, error: retryAfter > 0 && Number.isFinite(retryAfter) ? { ...error, message: error.message + localizeRetryAfter(Math.ceil(retryAfter)) } : error };
  } catch {
    return { error: clientError('SERVICE_UNAVAILABLE') };
  }
}

// ─── API Methods ─────────────────────────────────────────────────────────────

export interface ClinicSettings {
  defaultLocale?: string;
  reactivationThresholdDays?: number;
  reviewRequestsEnabled?: boolean;
  reviewRequestDestinationUrl?: string | null;
  reviewRequestDelayHours?: number;
  reviewRequestCooldownDays?: number;
  aiPatientCommunicationMode?: 'staff_approval_required' | 'automatic_allowed';  [key: string]: unknown;
  commercialOffers?: Array<{ title: string; locale?: 'it' | 'en' | 'ar'; description: string; startsOn: string; endsOn: string; appointmentTypeIds?: number[]; eligibilityNote?: string }>;
}

export const api = {
  health: {
    check: () => apiFetch<HealthStatus>('/health'),
  },
  auth: {
    me: () => apiFetch<{ userId: number; email: string; role: string }>('/me'),

    login: (email: string, password: string) =>
      apiFetch<AuthResponse>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      }),

    register: (input: {
      email: string;
      password: string;
      name: string;
      organizationName?: string;
      clinicName?: string;
      timezone?: string;
      plan?: 'free' | 'trial';
    }) =>
      apiFetch<PendingEmailVerificationResponse>('/auth/register', {
        method: 'POST',
        body: JSON.stringify(input),
      }),

    logout: (refreshToken: string) =>
      apiFetch<{ loggedOut: boolean }>('/auth/logout', {
        method: 'POST',
        body: JSON.stringify({ refreshToken }),
      }),

    refresh: (refreshToken: string) =>
      apiFetch<AuthTokens>('/auth/refresh', {
        method: 'POST',
        body: JSON.stringify({ refreshToken }),
      }, true),

    requestPasswordReset: (email: string) =>
      apiFetch<{ sent: boolean }>('/auth/password-reset/request', {
        method: 'POST',
        body: JSON.stringify({ email }),
      }),

    confirmPasswordReset: (token: string, password: string) =>
      apiFetch<{ reset: boolean }>('/auth/password-reset/confirm', {
        method: 'POST',
        body: JSON.stringify({ token, password }),
      }),

    resendEmailVerification: (email: string) =>
      apiFetch<{ sent: true; cooldownSeconds: number }>('/auth/email-verification/resend', { method: 'POST', body: JSON.stringify({ email }) }),

    verifyEmail: (token: string) =>
      apiFetch<{ verified: true; alreadyVerified: boolean }>('/auth/email-verification/verify', { method: 'POST', body: JSON.stringify({ token }) }),
  },
  patients: {
    list: (clinicId: number, params?: { search?: string; status?: string; limit?: number; offset?: number }) => {
      const qs = new URLSearchParams();
      if (params?.search) qs.set('search', params.search);
      if (params?.status) qs.set('status', params.status);
      if (params?.limit) qs.set('limit', String(params.limit));
      if (params?.offset) qs.set('offset', String(params.offset));
      const query = qs.toString();
      return apiFetch<Shared.Patient[]>(`/patients/${clinicId}/patients${query ? '?' + query : ''}`);
    },

    get: (clinicId: number, patientId: number) =>
      apiFetch<Shared.Patient>(`/patients/${clinicId}/patients/${patientId}`),

    getClinicalHistory: (clinicId: number, patientId: number) =>
      apiFetch<ClinicalHistoryItem>(`/patients/${clinicId}/patients/${patientId}/clinical-history`),

    saveClinicalHistory: (clinicId: number, patientId: number, content: string) =>
      apiFetch<ClinicalHistoryItem>(`/patients/${clinicId}/patients/${patientId}/clinical-history`, {
        method: 'PUT', body: JSON.stringify({ content }),
      }),
    getClinicalTranscriptionStatus:(clinicId:number,patientId:number)=>apiFetch<{available:boolean;maxBytes:number;maxSeconds:number}>(`/patients/${clinicId}/patients/${patientId}/clinical-transcription/status`),
    transcribeClinicalAudio:async(clinicId:number,patientId:number,file:File)=>{const form=new FormData();form.set('file',file);const token=getAccessToken();const response=await fetch(`${API_BASE}/patients/${clinicId}/patients/${patientId}/clinical-transcription`,{method:'POST',headers:token?{Authorization:`Bearer ${token}`}:{},body:form});const payload=await response.json() as ApiResponse<{transcript:string;requestId:string}>;return payload.error?{...payload,error:localizeApiError(payload.error)}:payload;},

    listDiagnosticAttachments: (clinicId:number,patientId:number) => apiFetch<{items:DiagnosticAttachmentItem[];storage:{available:boolean;maxBytes:number}}>(`/patients/${clinicId}/patients/${patientId}/diagnostic-attachments`),
    uploadDiagnosticAttachment: async (clinicId:number,patientId:number,file:File,attachmentType:string,description:string) => {
      const form=new FormData(); form.set('file',file); form.set('attachmentType',attachmentType); form.set('description',description);
      const token=getAccessToken(); const response=await fetch(`${API_BASE}/patients/${clinicId}/patients/${patientId}/diagnostic-attachments`,{method:'POST',headers:token?{Authorization:`Bearer ${token}`}:{},body:form});
      const payload=await response.json() as ApiResponse<DiagnosticAttachmentItem>; return payload.error?{...payload,error:localizeApiError(payload.error)}:payload;
    },
    deleteDiagnosticAttachment: (clinicId:number,patientId:number,id:number) => apiFetch<{deleted:boolean}>(`/patients/${clinicId}/patients/${patientId}/diagnostic-attachments/${id}`,{method:'DELETE'}),
    downloadDiagnosticAttachment: async (clinicId:number,patientId:number,id:number) => {
      const token=getAccessToken(); const response=await fetch(`${API_BASE}/patients/${clinicId}/patients/${patientId}/diagnostic-attachments/${id}/content`,{headers:token?{Authorization:`Bearer ${token}`}:{}});
      if(!response.ok) throw new Error('Attachment download failed'); return response.blob();
    },

    create: (clinicId: number, input: {
      firstName: string;
      lastName: string;
      email?: string;
      phone: string;
      dateOfBirth?: string;
      notes?: string;
      consentMarketing?: boolean;
      consentSms?: boolean;
      consentEmail?: boolean;
      consentWhatsapp?: boolean;
      aiCommunicationPolicy?: 'clinic_default' | 'staff_approval_required' | 'automatic_allowed';
    }) =>
      apiFetch<Shared.Patient>(`/patients/${clinicId}/patients`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),

    update: (clinicId: number, patientId: number, input: Record<string, unknown>) =>
      apiFetch<Shared.Patient>(`/patients/${clinicId}/patients/${patientId}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),

    delete: (clinicId: number, patientId: number) =>
      apiFetch<{ deleted: boolean }>(`/patients/${clinicId}/patients/${patientId}`, {
        method: 'DELETE',
      }),
  },
  appointments: {
    get: (clinicId: number, id: number) =>
      apiFetch<Shared.Appointment>(`/appointments/${clinicId}/appointments/${id}`),
    list: (clinicId: number, params?: { patientId?: number; providerId?: number; status?: string; startDate?: string; endDate?: string }) => {
      const qs = new URLSearchParams();
      if (params?.patientId) qs.set('patientId', String(params.patientId));
      if (params?.providerId) qs.set('providerId', String(params.providerId));
      if (params?.status) qs.set('status', params.status);
      if (params?.startDate) qs.set('startDate', params.startDate);
      if (params?.endDate) qs.set('endDate', params.endDate);
      const query = qs.toString();
      return apiFetch<Shared.Appointment[]>(`/appointments/${clinicId}/appointments${query ? '?' + query : ''}`);
    },
    create: (clinicId: number, input: { patientId: number; providerId: number; appointmentTypeId: number; startTime: string; endTime: string; notes?: string; source?: string; idempotencyKey?: string }) =>
      apiFetch<Shared.Appointment>(`/appointments/${clinicId}/appointments`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    confirm: (clinicId: number, id: number) =>
      apiFetch<Shared.Appointment>(`/appointments/${clinicId}/appointments/${id}/confirm`, { method: 'POST' }),
    checkIn: (clinicId: number, id: number) =>
      apiFetch<Shared.Appointment>(`/appointments/${clinicId}/appointments/${id}/check-in`, { method: 'POST' }),
    cancel: (clinicId: number, id: number, reason: string) =>
      apiFetch<Shared.Appointment>(`/appointments/${clinicId}/appointments/${id}/cancel`, { method: 'POST', body: JSON.stringify({ reason }) }),
    complete: (clinicId: number, id: number) =>
      apiFetch<Shared.Appointment>(`/appointments/${clinicId}/appointments/${id}/complete`, { method: 'POST' }),
    noShow: (clinicId: number, id: number) =>
      apiFetch<Shared.Appointment>(`/appointments/${clinicId}/appointments/${id}/no-show`, { method: 'POST' }),
    reschedule: (clinicId: number, id: number, input: { startTime: string; endTime: string; idempotencyKey?: string }) =>
      apiFetch<Shared.Appointment>(`/appointments/${clinicId}/appointments/${id}/reschedule`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    availability: (clinicId: number, providerId: number, startTime: string, endTime: string) => {
      const query = new URLSearchParams({ providerId: String(providerId), startTime, endTime });
      return apiFetch<{ available: boolean }>(`/appointments/${clinicId}/availability?${query.toString()}`);
    },
  },
  appointmentTypes: {
    list: (clinicId: number) =>
      apiFetch<{ id: number; name: string; description: string | null; durationMinutes: number; status: string; isRecallEligible: boolean; recallIntervalDays: number | null; priceMinorUnits: number | null; currency: 'EUR' | 'USD' | 'ILS' | null }[]>(`/appointments/${clinicId}/appointment-types`),
    create: (clinicId: number, input: { name: string; description?: string; durationMinutes: number; isRecallEligible?: boolean; recallIntervalDays?: number | null; priceMinorUnits?: number | null; currency?: 'EUR' | 'USD' | 'ILS' | null }) =>
      apiFetch<{ id: number; name: string; description: string | null; durationMinutes: number; status: string; isRecallEligible: boolean; recallIntervalDays: number | null }>(`/appointments/${clinicId}/appointment-types`, { method: 'POST', body: JSON.stringify(input) }),
    update: (clinicId: number, typeId: number, input: Record<string, unknown>) =>
      apiFetch<{ id: number; name: string; description: string | null; durationMinutes: number; status: string; isRecallEligible: boolean; recallIntervalDays: number | null }>(`/appointments/${clinicId}/appointment-types/${typeId}`, { method: 'PATCH', body: JSON.stringify(input) }),
    deactivate: (clinicId: number, typeId: number) =>
      apiFetch<{ deactivated: boolean }>(`/appointments/${clinicId}/appointment-types/${typeId}/deactivate`, { method: 'POST' }),
  },
  providers: {
    list: (clinicId: number) =>
      apiFetch<Shared.Provider[]>(`/providers/${clinicId}/providers`),
    getSchedule: (clinicId: number, providerId: number) =>
      apiFetch<ProviderSchedule[]>(`/providers/${clinicId}/providers/${providerId}/schedule`),
    setSchedule: (clinicId: number, providerId: number, schedules: ProviderSchedule[]) =>
      apiFetch<ProviderSchedule[]>(`/providers/${clinicId}/providers/${providerId}/schedule`, {
        method: 'PUT',
        body: JSON.stringify(schedules),
      }),
    create: (clinicId: number, input: { firstName: string; lastName: string; providerType: string; email?: string; phone?: string; notes?: string }) =>
      apiFetch<Shared.Provider>(`/providers/${clinicId}/providers`, { method: 'POST', body: JSON.stringify(input) }),
    update: (clinicId: number, providerId: number, input: Record<string, unknown>) =>
      apiFetch<Shared.Provider>(`/providers/${clinicId}/providers/${providerId}`, { method: 'PATCH', body: JSON.stringify(input) }),
    deactivate: (clinicId: number, providerId: number) =>
      apiFetch<Shared.Provider>(`/providers/${clinicId}/providers/${providerId}/deactivate`, { method: 'POST' }),
  },
  appointmentConfirmation: {
    queue: (clinicId: number) =>
      apiFetch<{
        items: Array<{
          id: number;
          patientId: number;
          patientName: string;
          providerId: number;
          providerName: string;
          appointmentTypeId: number | null;
          appointmentTypeName: string;
          startTime: string;
          endTime: string;
          status: 'pending';
          category: 'needs_attention_today' | 'upcoming';
        }>;
        summary: { total: number; needsAttentionToday: number; upcoming: number; windowDays: number };
      }>(`/receptionist/${clinicId}/appointment-confirmation-queue`),
  },
  appointmentAttendance: {
    queue: (clinicId: number) =>
      apiFetch<{
        items: Array<{
          id: number;
          patientId: number;
          patientName: string;
          providerId: number;
          providerName: string;
          appointmentTypeId: number | null;
          appointmentTypeName: string;
          startTime: string;
          endTime: string;
          status: 'confirmed';
          checkedInAt: string | null;
          checkedInByUserId: number | null;
          isPastUnresolved: boolean;
        }>;
        summary: { total: number; checkedIn: number; pastUnresolved: number };
      }>(`/receptionist/${clinicId}/today-attendance`),
  },
  dashboard: {
    get: (clinicId: number) =>
      apiFetch<{
        counts: {
          totalPatients: number;
          activeProviders: number;
          todaysAppointments: number;
          pendingAppointments: number;
          confirmedAppointments: number;
          completedAppointments: number;
        };
        todaysAppointments: Array<{
          id: number;
          patientName: string;
          providerName: string;
          startTime: string;
          endTime: string;
          status: string;
          appointmentTypeName: string;
        }>;
        recentActivity: Array<{
          action: string;
          resourceType: string;
          resourceId: string;
          timestamp: string;
          actorId: number | null;
        }>;
      }>(`/receptionist/${clinicId}/dashboard`),
  },
  conversations: {
    list: (clinicId: number, params?: { status?: string; channel?: string; patientId?: number; assignedTo?: number; inboxFilter?: string; language?: string; patientSearch?: string }) => {
      const qs = new URLSearchParams();
      if (params?.status) qs.set('status', params.status);
      if (params?.channel) qs.set('channel', params.channel);
      if (params?.patientId) qs.set('patientId', String(params.patientId));
      if (params?.assignedTo !== undefined) qs.set('assignedTo', String(params.assignedTo));
      if (params?.inboxFilter) qs.set('inboxFilter', params.inboxFilter);
      if (params?.language) qs.set('language', params.language);
      if (params?.patientSearch) qs.set('patientSearch', params.patientSearch);
      const query = qs.toString();
      return apiFetch<ConversationItem[]>(`/conversations/${clinicId}/conversations${query ? '?' + query : ''}`);
    },
    get: (clinicId: number, id: number) =>
      apiFetch<ConversationItem>(`/conversations/${clinicId}/conversations/${id}`),
    create: (clinicId: number, input: { channel: string; patientId?: number; subject?: string }) =>
      apiFetch<ConversationItem>(`/conversations/${clinicId}/conversations`, { method: 'POST', body: JSON.stringify(input) }),
    messages: (clinicId: number, conversationId: number) =>
      apiFetch<MessageItem[]>(`/conversations/${clinicId}/conversations/${conversationId}/messages`),
    markRead: (clinicId: number, conversationId: number) =>
      apiFetch<{ markedRead: number }>(`/conversations/${clinicId}/conversations/${conversationId}/read`, { method: 'POST' }),
    sendMessage: (clinicId: number, conversationId: number, input: { body: string; senderType?: string; contentType?: string }) =>
      apiFetch<MessageItem>(`/conversations/${clinicId}/conversations/${conversationId}/messages`, { method: 'POST', body: JSON.stringify(input) }),
    sendEmailReply: (clinicId: number, conversationId: number, body: string) =>
      apiFetch<CommunicationDraftItem>(`/conversations/${clinicId}/conversations/${conversationId}/email-reply`, { method: 'POST', body: JSON.stringify({ body }) }),
    assign: (clinicId: number, conversationId: number, staffUserId: number) =>
      apiFetch<ConversationItem>(`/conversations/${clinicId}/conversations/${conversationId}/assign`, { method: 'POST', body: JSON.stringify({ staffUserId }) }),
    linkPatient: (clinicId: number, conversationId: number, patientId: number, continueWithAgent = false) =>
      apiFetch<{ conversation: ConversationItem; resumedLatestInbound: boolean; resumeOutcome?: string }>(`/conversations/${clinicId}/conversations/${conversationId}/link-patient`, { method: 'POST', body: JSON.stringify({ patientId, continueWithAgent }) }),
    updateStatus: (clinicId: number, conversationId: number, status: string) =>
      apiFetch<ConversationItem>(`/conversations/${clinicId}/conversations/${conversationId}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),
  },
  staff: {
    list: (clinicId: number, params?: { role?: string; status?: string; search?: string }) => {
      const qs = new URLSearchParams();
      if (params?.role) qs.set('role', params.role);
      if (params?.status) qs.set('status', params.status);
      if (params?.search) qs.set('search', params.search);
      const query = qs.toString();
      return apiFetch<StaffMemberItem[]>(`/staff/${clinicId}/staff${query ? '?' + query : ''}`);
    },
    listInvitations: (clinicId: number) =>
      apiFetch<StaffInvitationItem[]>(`/staff/${clinicId}/staff/invitations`),
    invite: (clinicId: number, input: { email: string; role: 'staff' | 'provider' }) =>
      apiFetch<{ invitation: StaffInvitationItem }>(`/staff/${clinicId}/staff/invitations`, { method: 'POST', body: JSON.stringify(input) }),
    resendInvitation: (clinicId: number, invitationId: number) =>
      apiFetch<{ invitation: StaffInvitationItem }>(`/staff/${clinicId}/staff/invitations/${invitationId}/resend`, { method: 'POST' }),
    cancelInvitation: (clinicId: number, invitationId: number) =>
      apiFetch<{ cancelled: boolean }>(`/staff/${clinicId}/staff/invitations/${invitationId}/cancel`, { method: 'POST' }),
    updateRole: (clinicId: number, userId: number, role: 'staff' | 'provider') =>
      apiFetch<StaffMemberItem>(`/staff/${clinicId}/staff/${userId}/role`, { method: 'PATCH', body: JSON.stringify({ role }) }),
    deactivate: (clinicId: number, userId: number) =>
      apiFetch<{ deactivated: boolean }>(`/staff/${clinicId}/staff/${userId}/deactivate`, { method: 'POST' }),
    reactivate: (clinicId: number, userId: number) =>
      apiFetch<{ reactivated: boolean }>(`/staff/${clinicId}/staff/${userId}/reactivate`, { method: 'POST' }),
    acceptInvitation: (input: {
      token: string;
      password: string;
      name?: string;
      providerProfile?: { firstName: string; lastName: string; providerType: 'dentist' | 'hygienist' };
    }) => apiFetch<{ accepted: true; role: 'staff' | 'provider'; accountCreated: boolean; clinicId: number }>(
      '/staff-invitations/accept',
      { method: 'POST', body: JSON.stringify(input) },
    ),
  },
  organizations: {
    get: () =>
      apiFetch<{ id: number; name: string; status: string; plan: string; createdAt: string; updatedAt: string }>(`/organizations`),
    update: (input: { name?: string; status?: string; plan?: string }) =>
      apiFetch<{ id: number; name: string; status: string; plan: string; createdAt: string; updatedAt: string }>(`/organizations`, { method: 'PATCH', body: JSON.stringify(input) }),
  },
  clinics: {
    getEmailIdentity: (clinicId: number) => apiFetch<{ identity: ClinicEmailIdentity | null; domain: string | null; suggestion: string }>(`/clinics/${clinicId}/email-identity`),
    saveEmailIdentity: (clinicId: number, localPart: string) => apiFetch<ClinicEmailIdentity>(`/clinics/${clinicId}/email-identity`, { method: 'PUT', body: JSON.stringify({ localPart }) }),
    getProfile: (clinicId: number) =>
      apiFetch<{ id: number; name: string; slug: string; timezone: string; status: string; settings: Record<string, unknown>; createdAt: string; updatedAt: string }>(`/clinics/${clinicId}`),
    updateProfile: (clinicId: number, input: { name?: string; timezone?: string }) =>
      apiFetch<{ id: number; name: string; slug: string; timezone: string; status: string; settings: Record<string, unknown>; createdAt: string; updatedAt: string }>(`/clinics/${clinicId}`, { method: 'PATCH', body: JSON.stringify(input) }),
    getSettings: (clinicId: number) =>
      apiFetch<ClinicSettings>(`/clinics/${clinicId}/settings`),
    updateSettings: (clinicId: number, input: Partial<ClinicSettings>) =>
      apiFetch<ClinicSettings>(`/clinics/${clinicId}/settings`, { method: 'PATCH', body: JSON.stringify(input) }),
    getBusinessHours: (clinicId: number) =>
      apiFetch<BusinessHours[]>(`/clinics/${clinicId}/business-hours`),
    setBusinessHours: (clinicId: number, hours: BusinessHours[]) =>
      apiFetch<BusinessHours[]>(`/clinics/${clinicId}/business-hours`, { method: 'PUT', body: JSON.stringify(hours) }),
  },
  billing: {
    getSubscription: (clinicId: number) =>
      apiFetch<BillingSubscriptionStatus>(`/billing/${clinicId}/subscription`),
    createProfessionalCheckout: (clinicId: number, input: { cycle: 'monthly' | 'annual' }) =>
      apiFetch<ProfessionalCheckoutSession>(`/billing/${clinicId}/professional-checkout`, {
        method: 'POST',
        body: JSON.stringify({ cycle: input.cycle }),
      }),

    createPortal: (clinicId: number) =>
      apiFetch<{ id: string; url: string }>(`/billing/${clinicId}/portal`, {
        method: 'POST',
        body: JSON.stringify({ returnUrl: `${window.location.origin}${window.location.pathname}#/app/settings?section=billing` }),
      }),
    cancelAtPeriodEnd: (clinicId: number) =>
      apiFetch<BillingSubscriptionStatus>(`/billing/${clinicId}/cancel`, { method: 'POST' }),
    getPilotEntitlement: (clinicId: number) =>
      apiFetch<PilotEntitlement>(`/billing/${clinicId}/pilot-entitlement`),
    grantManagedPilot: (clinicId: number) =>
      apiFetch<ManagedPilotGrant>(`/billing/${clinicId}/pilot/grant`, { method: 'POST' }),
  },
  careJourneys: {
    list: (clinicId: number, params?: { patientId?: number; status?: string; followUpDueOnly?: boolean }) => {
      const query = new URLSearchParams();
      if (params?.patientId) query.set('patientId', String(params.patientId));
      if (params?.status) query.set('status', params.status);
      if (params?.followUpDueOnly) query.set('followUpDueOnly', 'true');
      const suffix = query.toString();
      return apiFetch<CareJourney[]>(`/care-journeys/${clinicId}/care-journeys${suffix ? `?${suffix}` : ''}`);
    },
    get: (clinicId: number, journeyId: number) => apiFetch<CareJourney>(`/care-journeys/${clinicId}/care-journeys/${journeyId}`),
    metrics: (clinicId: number) => apiFetch<CareJourneyMetrics>(`/care-journeys/${clinicId}/care-journeys/metrics`),
    create: (clinicId: number, input: { patientId: number; administrativeSummary: string; administrativeValue?: number | null; followUpDueAt?: string | null; revenueOpportunityId?: number | null; items?: Array<{ label: string }> }) =>
      apiFetch<CareJourney>(`/care-journeys/${clinicId}/care-journeys`, { method: 'POST', body: JSON.stringify(input) }),
    update: (clinicId: number, journeyId: number, input: { administrativeSummary?: string; administrativeValue?: number | null; followUpDueAt?: string | null; followUpStatus?: string }) =>
      apiFetch<CareJourney>(`/care-journeys/${clinicId}/care-journeys/${journeyId}`, { method: 'PATCH', body: JSON.stringify(input) }),
    transition: (clinicId: number, journeyId: number, status: CareJourney['status']) =>
      apiFetch<CareJourney>(`/care-journeys/${clinicId}/care-journeys/${journeyId}/status`, { method: 'POST', body: JSON.stringify({ status }) }),
    addItem: (clinicId: number, journeyId: number, label: string) =>
      apiFetch<CareJourneyItem>(`/care-journeys/${clinicId}/care-journeys/${journeyId}/items`, { method: 'POST', body: JSON.stringify({ label }) }),
    updateItem: (clinicId: number, journeyId: number, itemId: number, status: CareJourneyItem['status']) =>
      apiFetch<CareJourneyItem>(`/care-journeys/${clinicId}/care-journeys/${journeyId}/items/${itemId}`, { method: 'PATCH', body: JSON.stringify({ status }) }),
    linkAppointment: (clinicId: number, journeyId: number, appointmentId: number, careJourneyItemId: number | null = null) =>
      apiFetch<CareJourney>(`/care-journeys/${clinicId}/care-journeys/${journeyId}/appointments`, { method: 'POST', body: JSON.stringify({ appointmentId, careJourneyItemId }) }),
  },
  imports: {
    previewConciergePms: (clinicId: number, source: string) =>
      apiFetch<PmsImportPreview>(`/imports/${clinicId}/concierge-pms/preview`, { method: 'POST', body: JSON.stringify({ source }) }),
    confirmConciergePms: (clinicId: number, source: string, confirmationToken: string) =>
      apiFetch<PmsImportResult>(`/imports/${clinicId}/concierge-pms/confirm`, { method: 'POST', body: JSON.stringify({ source, confirmationToken }) }),
    getPilotActivation: (clinicId: number) =>
      apiFetch<PmsPilotActivation>(`/imports/${clinicId}/concierge-pms/pilot-activation`),
    activatePilot: (clinicId: number, jobId: number) =>
      apiFetch<PmsPilotActivation>(`/imports/${clinicId}/concierge-pms/pilot-activation`, { method: 'POST', body: JSON.stringify({ jobId }) }),
  },
  privacy: {
    getPatientData: (clinicId: number, patientId: number) =>
      apiFetch<{
        patient: {
          id: number; firstName: string; lastName: string; email: string | null;
          phone: string | null; dateOfBirth: string | null; status: string;
          consentMarketing: boolean; consentSms: boolean; consentEmail: boolean;
          consentWhatsapp: boolean; notes: string | null; createdAt: string;
        };
        appointments: Array<{ id: number; providerName: string; startTime: string; endTime: string; status: string; appointmentTypeName: string | null }>;
        conversations: Array<{ id: number; channel: string; status: string; createdAt: string }>;
        messages: Array<{ id: number; direction: string; senderType: string; body: string | null; createdAt: string }>;
        notifications: Array<{ id: number; type: string; title: string; body: string; deliveryStatus: string; createdAt: string }>;
        exportedAt: string;
      }>(`/privacy/${clinicId}/privacy/patients/${patientId}/access`),
    erasePatientData: (clinicId: number, patientId: number) =>
      apiFetch<{ erased: boolean; erasedAt: string }>(`/privacy/${clinicId}/privacy/patients/${patientId}/erasure`, { method: 'POST' }),
    exportPatientData: (clinicId: number, patientId: number) =>
      apiFetch<Record<string, unknown>>(`/privacy/${clinicId}/privacy/patients/${patientId}/export`),
    getConsent: (clinicId: number, patientId: number) =>
      apiFetch<{ consentMarketing: boolean; consentSms: boolean; consentEmail: boolean; consentWhatsapp: boolean; consentUpdatedAt: string | null }>(`/privacy/${clinicId}/privacy/patients/${patientId}/consent`),
    updateConsent: (clinicId: number, patientId: number, input: { consentMarketing?: boolean; consentSms?: boolean; consentEmail?: boolean; consentWhatsapp?: boolean }) =>
      apiFetch<{ consentMarketing: boolean; consentSms: boolean; consentEmail: boolean; consentWhatsapp: boolean; consentUpdatedAt: string | null }>(`/privacy/${clinicId}/privacy/patients/${patientId}/consent`, { method: 'PATCH', body: JSON.stringify(input) }),
    getRetentionPolicy: (clinicId: number) =>
      apiFetch<{ patientDataRetentionDays: number; auditLogRetentionDays: number; conversationRetentionDays: number; deletedDataGracePeriodDays: number }>(`/privacy/${clinicId}/privacy/retention`),
  },
  notifications: {
    list: (clinicId: number, params?: { userId?: number; unread?: boolean; type?: string }) => {
      const qs = new URLSearchParams();
      if (params?.userId) qs.set('userId', String(params.userId));
      if (params?.unread) qs.set('unread', 'true');
      if (params?.type) qs.set('type', params.type);
      const query = qs.toString();
      return apiFetch<NotificationItem[]>(`/notifications/${clinicId}/notifications${query ? '?' + query : ''}`);
    },
    markAsRead: (clinicId: number, notificationId: number) =>
      apiFetch<NotificationItem>(`/notifications/${clinicId}/notifications/${notificationId}/read`, { method: 'POST' }),
    markAllAsRead: (clinicId: number, userId: number) =>
      apiFetch<{ markedRead: number }>(`/notifications/${clinicId}/notifications/read-all`, { method: 'POST', body: JSON.stringify({ userId }) }),
  },
  knowledgeBase: {
    list: (clinicId: number, params?: { status?: string; category?: string; search?: string }) => {
      const qs = new URLSearchParams();
      if (params?.status) qs.set('status', params.status);
      if (params?.category) qs.set('category', params.category);
      if (params?.search) qs.set('search', params.search);
      const query = qs.toString();
      return apiFetch<KbItem[]>(`/knowledge-base/${clinicId}/knowledge-base${query ? '?' + query : ''}`);
    },
    get: (clinicId: number, itemId: number) =>
      apiFetch<KbItem>(`/knowledge-base/${clinicId}/knowledge-base/${itemId}`),
    create: (clinicId: number, input: { category: string; title: string; content: string }) =>
      apiFetch<KbItem>(`/knowledge-base/${clinicId}/knowledge-base`, { method: 'POST', body: JSON.stringify(input) }),
    update: (clinicId: number, itemId: number, input: { category?: string; title?: string; content?: string }) =>
      apiFetch<KbItem>(`/knowledge-base/${clinicId}/knowledge-base/${itemId}`, { method: 'PATCH', body: JSON.stringify(input) }),
    submitForReview: (clinicId: number, itemId: number) =>
      apiFetch<KbItem>(`/knowledge-base/${clinicId}/knowledge-base/${itemId}/submit`, { method: 'POST' }),
    approve: (clinicId: number, itemId: number) =>
      apiFetch<KbItem>(`/knowledge-base/${clinicId}/knowledge-base/${itemId}/approve`, { method: 'POST' }),
    reject: (clinicId: number, itemId: number) =>
      apiFetch<KbItem>(`/knowledge-base/${clinicId}/knowledge-base/${itemId}/reject`, { method: 'POST' }),
    archive: (clinicId: number, itemId: number) =>
      apiFetch<KbItem>(`/knowledge-base/${clinicId}/knowledge-base/${itemId}/archive`, { method: 'POST' }),
  },
  communication: {
    templates: {
      list: (clinicId: number, status?: string) => apiFetch<CommunicationTemplateItem[]>(`/communication/${clinicId}/templates${status ? `?status=${encodeURIComponent(status)}` : ''}`),
      get: (clinicId: number, templateId: number) => apiFetch<CommunicationTemplateItem>(`/communication/${clinicId}/templates/${templateId}`),
      create: (clinicId: number, input: { templateKey: string; name: string; purpose: string; channel: string; locale: string; content: string; allowedVariables: string[]; providerTemplateName?: string }) =>
        apiFetch<CommunicationTemplateItem>(`/communication/${clinicId}/templates`, { method: 'POST', body: JSON.stringify(input) }),
      newVersion: (clinicId: number, templateId: number, input: Partial<{ templateKey: string; name: string; purpose: string; channel: string; locale: string; content: string; allowedVariables: string[]; providerTemplateName: string }>) =>
        apiFetch<CommunicationTemplateItem>(`/communication/${clinicId}/templates/${templateId}/version`, { method: 'POST', body: JSON.stringify(input) }),
      submit: (clinicId: number, templateId: number) => apiFetch<CommunicationTemplateItem>(`/communication/${clinicId}/templates/${templateId}/submit`, { method: 'POST' }),
      approve: (clinicId: number, templateId: number) => apiFetch<CommunicationTemplateItem>(`/communication/${clinicId}/templates/${templateId}/approve`, { method: 'POST' }),
      reject: (clinicId: number, templateId: number) => apiFetch<CommunicationTemplateItem>(`/communication/${clinicId}/templates/${templateId}/reject`, { method: 'POST' }),
      archive: (clinicId: number, templateId: number) => apiFetch<CommunicationTemplateItem>(`/communication/${clinicId}/templates/${templateId}/archive`, { method: 'POST' }),
      preview: (clinicId: number, templateId: number, variables: Record<string, string | number>) => apiFetch<{ templateId: number; templateVersion: number; locale: string; renderedContent: string; variableNames: string[] }>(`/communication/${clinicId}/templates/${templateId}/preview`, { method: 'POST', body: JSON.stringify({ variables }) }),
    },
    eligibility: (clinicId: number, input: { patientId: number; purpose: string; channel: string }) =>
      apiFetch<{ allowed: boolean; reason: string; patientId: number; purpose: string | null; channel: string }>(`/communication/${clinicId}/eligibility?${new URLSearchParams({ patientId: String(input.patientId), purpose: input.purpose, channel: input.channel }).toString()}`),
    drafts: {
      list: (clinicId: number, status?: string) => apiFetch<CommunicationDraftItem[]>(`/communication/${clinicId}/drafts${status ? `?status=${encodeURIComponent(status)}` : ''}`),
      get: (clinicId: number, draftId: number) => apiFetch<CommunicationDraftItem>(`/communication/${clinicId}/drafts/${draftId}`),
      editBody: (clinicId: number, draftId: number, body: string) => apiFetch<CommunicationDraftItem>(`/communication/${clinicId}/drafts/${draftId}/body`, { method: 'PATCH', body: JSON.stringify({ body }) }),
      create: (clinicId: number, input: { patientId: number; purpose: string; channel: string; locale?: string; variables: Record<string, string | number>; revenueOpportunityId?: number | null }) => apiFetch<CommunicationDraftItem>(`/communication/${clinicId}/drafts`, { method: 'POST', body: JSON.stringify(input) }),
      preview: (clinicId: number, input: { patientId: number; purpose: string; channel: string; locale?: string; variables: Record<string, string | number> }) => apiFetch<{ templateId: number; templateVersion: number; locale: string; renderedContent: string; variableNames: string[] }>(`/communication/${clinicId}/drafts/preview`, { method: 'POST', body: JSON.stringify(input) }),
      approve: (clinicId: number, draftId: number) => apiFetch<CommunicationDraftItem>(`/communication/${clinicId}/drafts/${draftId}/approve`, { method: 'POST' }),
      reject: (clinicId: number, draftId: number) => apiFetch<CommunicationDraftItem>(`/communication/${clinicId}/drafts/${draftId}/reject`, { method: 'POST' }),
      markApprovedForSend: (clinicId: number, draftId: number) => apiFetch<CommunicationDraftItem>(`/communication/${clinicId}/drafts/${draftId}/approved-for-send`, { method: 'POST' }),
      revokeApproval: (clinicId: number, draftId: number) => apiFetch<CommunicationDraftItem>(`/communication/${clinicId}/drafts/${draftId}/revoke-approval`, { method: 'POST' }),
      createResendOutboxIntent: (clinicId: number, draftId: number) =>
        apiFetch<{ id: number; state: string; communicationDraftId: number | null; providerMessageId: string | null }>(`/messaging/${clinicId}/messaging/drafts/${draftId}/outbox-resend`, { method: 'POST' }),
      dispatchResendOutboxIntent: (clinicId: number, intentId: number) =>
        apiFetch<{ id: number; state: string; communicationDraftId: number | null; providerMessageId: string | null }>(`/messaging/${clinicId}/messaging/outbox/${intentId}/dispatch-resend`, { method: 'POST' }),
      fromRevenue: (clinicId: number, input: { opportunityId: number; patientId: number; purpose: string; channel: string; locale?: string; variables: Record<string, string | number> }) => apiFetch<CommunicationDraftItem>(`/communication/${clinicId}/drafts/from-revenue`, { method: 'POST', body: JSON.stringify(input) }),
    },
    outbox: {
      list: (clinicId: number, channel?: string) => apiFetch<CommunicationOutboxItem[]>(`/messaging/${clinicId}/messaging/outbox${channel ? `?channel=${encodeURIComponent(channel)}` : ''}`),
    },
  },
  whatsapp: {
    getConnection: (clinicId: number) =>
      apiFetch<WhatsAppConnectionStatus>(`/messaging/${clinicId}/whatsapp/connection`),
    startOnboarding: (clinicId: number) =>
      apiFetch<WhatsAppOnboardingIntent>(`/messaging/${clinicId}/whatsapp/onboarding`, { method: 'POST' }),
    completeOnboarding: (clinicId: number, input: { authorizationCode: string; businessAccountId: string; phoneNumberId: string }) =>
      apiFetch<WhatsAppConnectionMetadata>(`/messaging/${clinicId}/whatsapp/onboarding/complete`, { method: 'POST', body: JSON.stringify(input) }),
    activateSandbox: (clinicId: number) =>
      apiFetch<WhatsAppConnectionMetadata>(`/messaging/${clinicId}/whatsapp/sandbox/activate`, { method: 'POST' }),
    disconnectConnection: (clinicId: number) =>
      apiFetch<WhatsAppConnectionMetadata>(`/messaging/${clinicId}/whatsapp/connection`, { method: 'DELETE' }),
    createApprovedDraftIntent: (clinicId: number, draftId: number) =>
      apiFetch<{ id: number; state: string; communicationDraftId: number | null; providerMessageId: string | null }>(`/messaging/${clinicId}/drafts/${draftId}/outbox`, { method: 'POST' }),
    dispatchApprovedIntent: (clinicId: number, intentId: number) =>
      apiFetch<{ id: number; state: string; communicationDraftId: number | null; providerMessageId: string | null }>(`/messaging/${clinicId}/outbox/${intentId}/dispatch-whatsapp`, { method: 'POST' }),
  },
  whatsappAgent: {
    getConfig: (clinicId: number) => apiFetch<WhatsAppAgentConfig>(`/whatsapp-agent/${clinicId}/config`),
    overview: (clinicId: number) => apiFetch<Record<string, unknown>>(`/whatsapp-agent/${clinicId}/overview`),
    playbooks: (clinicId: number) => apiFetch<Array<Record<string, unknown>>>(`/whatsapp-agent/${clinicId}/playbooks`),
    updatePlaybook: (clinicId: number, id: number, input: { active?: boolean; approvalRequired?: boolean; maxFollowUps?: number; cooldownHours?: number }) => apiFetch<Record<string, unknown>>(`/whatsapp-agent/${clinicId}/playbooks/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    tasks: (clinicId: number) => apiFetch<Array<Record<string, unknown>>>(`/whatsapp-agent/${clinicId}/tasks`),
    updateConfig: (clinicId: number, input: Partial<WhatsAppAgentConfig> & { activationAcknowledgement?: boolean }) => apiFetch<WhatsAppAgentConfig>(`/whatsapp-agent/${clinicId}/config`, { method: 'PATCH', body: JSON.stringify(input) }),
    activate: (clinicId: number) => apiFetch<WhatsAppAgentConfig>(`/whatsapp-agent/${clinicId}/activate`, { method: 'POST', body: JSON.stringify({ acknowledgement: true }) }),
    controlConversation: (clinicId: number, conversationId: number, control: 'take_over' | 'assign_to_staff' | 'pause_agent' | 'resume_agent' | 'release_to_agent' | 'return_to_agent' | 'escalate' | 'mark_resolved', reason?: string) => apiFetch<Record<string, unknown>>(`/whatsapp-agent/${clinicId}/conversations/${conversationId}/control`, { method: 'POST', body: JSON.stringify({ control, reason }) }),
    intelligence: (clinicId: number, conversationId: number) => apiFetch<Record<string, unknown>>(`/whatsapp-agent/${clinicId}/conversations/${conversationId}/intelligence`),
    patientMemory: (clinicId: number, patientId: number) => apiFetch<{ context: Record<string, unknown> | null; items: WhatsAppAgentMemoryItem[] }>(`/whatsapp-agent/${clinicId}/patients/${patientId}/memory`),
    createMemory: (clinicId: number, patientId: number, input: { category: string; valueSummary: string; source: string; confidenceCategory?: 'high' | 'needs_confirmation' | 'unavailable'; status?: 'draft' | 'approved' | 'rejected' | 'archived' }) => apiFetch<WhatsAppAgentMemoryItem>(`/whatsapp-agent/${clinicId}/patients/${patientId}/memory`, { method: 'POST', body: JSON.stringify(input) }),
    updateMemory: (clinicId: number, patientId: number, memoryId: number, input: Record<string, unknown>) => apiFetch<WhatsAppAgentMemoryItem>(`/whatsapp-agent/${clinicId}/patients/${patientId}/memory/${memoryId}`, { method: 'PATCH', body: JSON.stringify(input) }),
    forgetMemory: (clinicId: number, patientId: number, memoryId: number) => apiFetch<{ deleted: boolean }>(`/whatsapp-agent/${clinicId}/patients/${patientId}/memory/${memoryId}`, { method: 'DELETE' }),
    deactivate: (clinicId: number) => apiFetch<WhatsAppAgentConfig>(`/whatsapp-agent/${clinicId}/deactivate`, { method: 'POST' }),
    listKnowledge: (clinicId: number, locale?: 'it' | 'en' | 'ar') => apiFetch<WhatsAppAgentKnowledgeItem[]>(`/whatsapp-agent/${clinicId}/knowledge${locale ? `?locale=${locale}` : ''}`),
    createKnowledge: (clinicId: number, input: { category: string; locale: 'it' | 'en' | 'ar'; title: string; content: string }) => apiFetch<WhatsAppAgentKnowledgeItem>(`/whatsapp-agent/${clinicId}/knowledge`, { method: 'POST', body: JSON.stringify(input) }),
    approveKnowledge: (clinicId: number, itemId: number) => apiFetch<WhatsAppAgentKnowledgeItem>(`/whatsapp-agent/${clinicId}/knowledge/${itemId}/approve`, { method: 'POST' }),
    activity: (clinicId: number, limit = 50) => apiFetch<WhatsAppAgentEventItem[]>(`/whatsapp-agent/${clinicId}/activity?limit=${limit}`),
    voiceMedia: (clinicId: number, conversationId?: number, limit = 50) => apiFetch<WhatsAppVoiceMediaItem[]>(`/whatsapp-agent/${clinicId}/voice-media?${conversationId ? `conversationId=${conversationId}&` : ''}limit=${limit}`),
    processVoiceMedia: (clinicId: number, mediaId: number) => apiFetch<WhatsAppVoiceMediaItem>(`/whatsapp-agent/${clinicId}/voice-media/${mediaId}/process`, { method: 'POST' }),
    deleteVoiceMedia: (clinicId: number, mediaId: number) => apiFetch<{ deleted: boolean }>(`/whatsapp-agent/${clinicId}/voice-media/${mediaId}`, { method: 'DELETE' }),
    createFeedback: (clinicId: number, input: { conversationId: number; eventId?: number; outcome: 'helpful' | 'incorrect' | 'missing_information' | 'escalate'; correction?: string }) => apiFetch<Record<string, unknown>>(`/whatsapp-agent/${clinicId}/feedback`, { method: 'POST', body: JSON.stringify(input) }),
    shadowPlan: (clinicId: number, conversationId: number, input: { message: string; language: 'it' | 'en' | 'ar' }) => apiFetch<Record<string, unknown>>(`/whatsapp-agent/${clinicId}/conversations/${conversationId}/shadow-plan`, { method: 'POST', body: JSON.stringify(input) }),
  },
  aiCopilot: {
    prepareDraft: (clinicId: number, conversationId: number, input: { purpose: string; channel: string; locale?: 'it' | 'en' | 'ar'; variables: Record<string, string | number>; staffNote?: string }) =>
      apiFetch<{ draft: { id: number; status: string; renderedBody: string }; providerId: 'deterministic_local' | 'live_llm'; mode: 'deterministic_local' | 'live_llm'; staffBrief: string; knowledgeItemIds: number[]; confidence: number }>(`/ai/${clinicId}/conversations/${conversationId}/ai/copilot-draft`, { method: 'POST', body: JSON.stringify(input) }),
  },
  aiConfig: {
    get: (clinicId: number) =>
      apiFetch<AiConfigItem>(`/ai/${clinicId}/ai/config`),
    update: (clinicId: number, input: { isEnabled?: boolean; escalationThreshold?: number; maxTurns?: number; autoHandoffAfterMinutes?: number; channelModes?: Partial<AiConfigItem['channelModes']> }) =>
      apiFetch<AiConfigItem>(`/ai/${clinicId}/ai/config`, { method: 'PATCH', body: JSON.stringify(input) }),
  },
  automationRules: {
    list: (clinicId: number) =>
      apiFetch<AutomationRuleItem[]>(`/automation/${clinicId}/rules`),
    update: (clinicId: number, ruleId: number, input: { enabled?: boolean; approvalRequired?: boolean; cooldownMinutes?: number }) =>
      apiFetch<AutomationRuleItem>(`/automation/${clinicId}/rules/${ruleId}`, { method: 'PATCH', body: JSON.stringify(input) }),
  },
  analytics: {
    appointments: (clinicId: number, params?: { startDate?: string; endDate?: string }) => {
      const qs = new URLSearchParams();
      if (params?.startDate) qs.set('startDate', params.startDate);
      if (params?.endDate) qs.set('endDate', params.endDate);
      const query = qs.toString();
      return apiFetch<AnalyticsAppt>(`/analytics/${clinicId}/analytics/appointments${query ? '?' + query : ''}`);
    },
    conversations: (clinicId: number, params?: { startDate?: string; endDate?: string }) => {
      const qs = new URLSearchParams();
      if (params?.startDate) qs.set('startDate', params.startDate);
      if (params?.endDate) qs.set('endDate', params.endDate);
      const query = qs.toString();
      return apiFetch<AnalyticsConv>(`/analytics/${clinicId}/analytics/conversations${query ? '?' + query : ''}`);
    },
    ai: (clinicId: number, params?: { startDate?: string; endDate?: string }) => {
      const qs = new URLSearchParams();
      if (params?.startDate) qs.set('startDate', params.startDate);
      if (params?.endDate) qs.set('endDate', params.endDate);
      const query = qs.toString();
      return apiFetch<AnalyticsAi>(`/analytics/${clinicId}/analytics/ai${query ? '?' + query : ''}`);
    },
    dashboard: (clinicId: number, params?: { startDate?: string; endDate?: string }) => {
      const qs = new URLSearchParams();
      if (params?.startDate) qs.set('startDate', params.startDate);
      if (params?.endDate) qs.set('endDate', params.endDate);
      const query = qs.toString();
      return apiFetch<AnalyticsOps>(`/analytics/${clinicId}/analytics/dashboard${query ? '?' + query : ''}`);
    },
  },
  auditLogs: {
    list: (params?: { clinicId?: number; action?: string; limit?: number; offset?: number }) => {
      const qs = new URLSearchParams();
      if (params?.clinicId) qs.set('clinicId', String(params.clinicId));
      if (params?.action) qs.set('action', params.action);
      if (params?.limit) qs.set('limit', String(params.limit));
      if (params?.offset) qs.set('offset', String(params.offset));
      const query = qs.toString();
      return apiFetch<AuditLogItem[]>(`/audit${query ? '?' + query : ''}`);
    },
  },
  revenue: {
    recoveryFollowUps: (clinicId: number, category: RecoveryFollowUpCategory = 'all_open_recovery') =>
      apiFetch<RecoveryFollowUpQueue>(`/revenue/${clinicId}/recovery-follow-ups?category=${encodeURIComponent(category)}`),
    list: (clinicId: number, params?: { type?: string; status?: string; priority?: string }) => {
      const qs = new URLSearchParams();
      if (params?.type) qs.set('type', params.type);
      if (params?.status) qs.set('status', params.status);
      if (params?.priority) qs.set('priority', params.priority);
      const query = qs.toString();
      return apiFetch<RevenueOpportunityItem[]>(`/revenue/${clinicId}/revenue-opportunities${query ? '?' + query : ''}`);
    },
    summary: (clinicId: number) =>
      apiFetch<RevenueSummary>(`/revenue/${clinicId}/revenue-opportunities/summary`),
    analytics: (clinicId: number, params?: { startDate?: string; endDate?: string }) => {
      const qs = new URLSearchParams();
      if (params?.startDate) qs.set('startDate', params.startDate);
      if (params?.endDate) qs.set('endDate', params.endDate);
      const query = qs.toString();
      return apiFetch<RevenueAnalyticsResult>(`/revenue/${clinicId}/revenue-opportunities/analytics${query ? '?' + query : ''}`);
    },
    scan: (clinicId: number) =>
      apiFetch<{ created: number; skipped: number }>(`/revenue/${clinicId}/revenue-opportunities/scan`, { method: 'POST' }),
    bookCancelledSlotRecovery: (clinicId: number, opportunityId: number) =>
      apiFetch<{ opportunity: RevenueOpportunityItem; appointmentId: number; idempotent: boolean }>(
        `/revenue/${clinicId}/revenue-opportunities/${opportunityId}/book`,
        { method: 'POST' },
      ),
    updateStatus: (clinicId: number, opportunityId: number, status: string) =>
      apiFetch<RevenueOpportunityItem>(`/revenue/${clinicId}/revenue-opportunities/${opportunityId}`, { method: 'PATCH', body: JSON.stringify({ status }) }),
    get: (clinicId: number, opportunityId: number) =>
      apiFetch<RevenueOpportunityItem>(`/revenue/${clinicId}/revenue-opportunities/${opportunityId}`),
    analyzeWithLuna: (clinicId: number, opportunityId: number) =>
      apiFetch<RevenueOpportunityAiAnalysis>(
        `/revenue/${clinicId}/revenue-opportunities/${opportunityId}/ai-analysis`,
        { method: 'POST' },
      ),
    updateWorkflow: (clinicId: number, opportunityId: number, input: {
      assigneeUserId?: number | null;
      nextAction?: 'review' | 'contact' | 'follow_up' | 'confirm_booking' | 'complete_follow_up' | null;
      dueAt?: string | null;
    }) => apiFetch<RevenueOpportunityItem>(
      `/revenue/${clinicId}/revenue-opportunities/${opportunityId}/workflow`,
      { method: 'PATCH', body: JSON.stringify(input) },
    ),
    activities: (clinicId: number, opportunityId: number) =>
      apiFetch<RevenueOpportunityActivityItem[]>(`/revenue/${clinicId}/revenue-opportunities/${opportunityId}/activities`),
    retentionQueue: (clinicId: number, category: RetentionQueueCategory = 'all') =>
      apiFetch<RetentionQueue>(`/revenue/${clinicId}/retention-queue?category=${encodeURIComponent(category)}`),
    recoveryContactAttempts: (clinicId: number, opportunityId: number) =>
      apiFetch<{ attempts: RevenueOpportunityActivityItem[]; tracking: RecoveryTrackingSummary }>(
        `/revenue/${clinicId}/revenue-opportunities/${opportunityId}/recovery-contact-attempts`,
      ),
    recordRecoveryContactAttempt: (clinicId: number, opportunityId: number, input: {
      channel: 'manual' | 'phone' | 'email' | 'whatsapp' | 'sms' | 'other';
      outcome: 'contacted' | 'no_answer' | 'patient_declined' | 'patient_will_rebook' | 'appointment_recovered' | 'invalid_contact';
      note?: string | null;
      nextActionAt?: string | null;
    }) => apiFetch<RecoveryContactAttemptResult>(
      `/revenue/${clinicId}/revenue-opportunities/${opportunityId}/recovery-contact-attempts`,
      { method: 'POST', body: JSON.stringify(input) },
    ),
    createActivity: (clinicId: number, opportunityId: number, input: {
      activityType: 'reviewed' | 'contacted' | 'follow_up' | 'booking_attempt' | 'booked' | 'completed' | 'dismissed';
      note?: string | null;
      outcome?: 'no_response' | 'interested' | 'booked' | 'not_interested' | 'follow_up_needed' | null;
    }) => apiFetch<RevenueOpportunityActivityItem>(
      `/revenue/${clinicId}/revenue-opportunities/${opportunityId}/activities`,
      { method: 'POST', body: JSON.stringify(input) },
    ),
    linkConvertedAppointment: (clinicId: number, opportunityId: number, appointmentId: number) =>
      apiFetch<RevenueOpportunityItem>(
        `/revenue/${clinicId}/revenue-opportunities/${opportunityId}/converted-appointment`,
        { method: 'POST', body: JSON.stringify({ appointmentId }) },
      ),
    prepareRecoveryDraft: (clinicId: number, opportunityId: number, input: {
      channel: 'webchat' | 'internal' | 'email' | 'sms' | 'whatsapp' | 'instagram' | 'facebook';
      locale?: 'it' | 'en' | 'ar';
      variables: Record<string, string | number>;
    }) => apiFetch<CommunicationDraftItem>(
      `/revenue/${clinicId}/revenue-opportunities/${opportunityId}/recovery-draft`,
      { method: 'POST', body: JSON.stringify(input) },
    ),
    prepareCancelledRecoveryDraft: (clinicId: number, opportunityId: number, input: {
      channel: 'webchat' | 'internal' | 'email' | 'sms' | 'whatsapp' | 'instagram' | 'facebook';
      locale?: 'it' | 'en' | 'ar';
      variables: Record<string, string | number>;
    }) => apiFetch<CommunicationDraftItem>(
      `/revenue/${clinicId}/revenue-opportunities/${opportunityId}/cancellation-recovery-draft`,
      { method: 'POST', body: JSON.stringify(input) },
    ),
  },
};
